import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { de } from 'date-fns/locale';
import { SnackbarProvider } from 'notistack';
import { CssBaseline } from '@mui/material';
import { AuthProvider } from './contexts/AuthContext';
import Dashboard from './components/Dashboard/Dashboard';
import Login from './pages/Login/Login';
import { PrivateRoute } from './components/PrivateRoute/PrivateRoute';
import { DailyReportsList } from './pages/DailyReports/DailyReportsList';
import { DailyReportForm } from './pages/DailyReports/DailyReportForm';
import { TimeTrackingList } from './pages/TimeTracking/TimeTrackingList';
import { TimeTrackingForm } from './pages/TimeTracking/TimeTrackingForm';
import TimeTrackingDetail from './pages/TimeTracking/TimeTrackingDetail';
import { ProjectsList } from './pages/Projects/ProjectsList';
import { ProjectForm } from './pages/Projects/ProjectForm';
import { SekurantenList } from './pages/Sekuranten/SekurantenList';
import { SekurantenForm } from './pages/Sekuranten/SekurantenForm';
import { SekurantenProjektForm } from './pages/Sekuranten/SekurantenProjektForm';
import { SekurantenProjektDetail } from './pages/Sekuranten/SekurantenProjektDetail';
import { UserManagement } from './pages/UserManagement/UserManagement';
import { theme } from './theme/theme';
import { Profile } from './pages/Profile/Profile';
import DashboardOverview from './pages/Dashboard/DashboardOverview';
import { ProjectDetail } from './pages/Projects/ProjectDetail';
import { ProjectCalendar } from './components/ProjectCalendar/ProjectCalendar';
import { DailyReportsListTagesbericht } from './pages/DailyReports/DailyReportsListTagesbericht';
import { DailyReportFormTagesbericht } from './pages/DailyReports/DailyReportFormTagesbericht';
import RegieReportDetail from './pages/RegieReports/RegieReportDetail';
import DailyReportDetail from './pages/DailyReports/DailyReportDetail';
import WorkTasksList from './pages/WorkTasks/WorkTasksList';
import WorkTaskForm from './pages/WorkTasks/WorkTaskForm';
import WorkTaskDetail from './pages/WorkTasks/WorkTaskDetail';

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
        <SnackbarProvider 
          maxSnack={3}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
        >
          <Router>
            <AuthProvider>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route element={<PrivateRoute />}>
                  <Route element={<Dashboard />}>
                    <Route path="/dashboard" element={<DashboardOverview />} />
                    <Route path="/regie-reports" element={<DailyReportsList />} />
                    <Route path="/regie-reports/new" element={<DailyReportForm />} />
                    <Route path="/regie-reports/:id/edit" element={<DailyReportForm />} />
                    <Route path="/regie-reports/:id" element={<RegieReportDetail />} />
                    <Route path="/time-tracking" element={<TimeTrackingList />} />
                    <Route path="/time-tracking/new" element={<TimeTrackingForm />} />
                    <Route path="/time-tracking/:id" element={<TimeTrackingDetail />} />
                    <Route path="/time-tracking/:id/edit" element={<TimeTrackingForm />} />
                    <Route path="/projects" element={<ProjectsList />} />
                    <Route path="/projects/new" element={<ProjectForm />} />
                    <Route path="/projects/:id/edit" element={<ProjectForm />} />
                    <Route path="/projects/:id" element={<ProjectDetail />} />
                    <Route path="/sekuranten" element={<SekurantenList />} />
                    <Route path="/sekuranten/new-projekt" element={<SekurantenProjektForm />} />
                    <Route path="/sekuranten-projekte/:id" element={<SekurantenProjektDetail />} />
                    <Route path="/sekuranten-projekte/:id/edit" element={<SekurantenProjektForm />} />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/users" element={<UserManagement />} />
                    <Route path="/user-management" element={<UserManagement />} />
                    <Route path="/project-calendar" element={<ProjectCalendar />} />
                    <Route path="/tagesberichte" element={<DailyReportsListTagesbericht />} />
                    <Route path="/tagesberichte/new" element={<DailyReportFormTagesbericht />} />
                    <Route path="/tagesberichte/:id/edit" element={<DailyReportFormTagesbericht />} />
                    <Route path="/tagesberichte/:id" element={<DailyReportDetail />} />
                    <Route path="/work-tasks" element={<WorkTasksList />} />
                    <Route path="/work-tasks/new" element={<WorkTaskForm />} />
                    <Route path="/work-tasks/:id" element={<WorkTaskDetail />} />
                    <Route path="/work-tasks/:id/edit" element={<WorkTaskForm />} />
                    {/* Root route - only redirect if exactly "/" */}
                    <Route index element={<Navigate to="/dashboard" replace />} />
                  </Route>
                </Route>
              </Routes>
            </AuthProvider>
          </Router>
        </SnackbarProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
};

export default App;
